import React, { useState } from "react";
import { Box, Button, Typography, Pagination, Paper, Stack, Chip, alpha } from "@mui/material";
import { Add as AddIcon, Medication as MedicineIcon } from "@mui/icons-material";
import AddMedicineModal from "./AddMedicineModal";
import EditMedicineModal from "./EditMedicineModal";
import { QK } from "../../../base/qk";
import { getMedicineList, deleteMedicine } from "../../../api/services/medication";
import { useQuery, useMutation, useQueryClient } from "react-query";
import ConfirmModal from "../../ConfirmModal/ConfirmModal";
import { useSnackbar } from "notistack";
import DataTable from "../../Table/DataTable";

const MedicineList = () => {
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { enqueueSnackbar } = useSnackbar();
  const medicinesPerPage = 10;

  const queryClient = useQueryClient();

  const { data: medicineLists, isLoading } = useQuery(
    [QK.medicineLists, currentPage],
    () => getMedicineList(currentPage),
    { keepPreviousData: true, enabled: !!currentPage }
  );

  const deleteMutation = useMutation(deleteMedicine, {
    onSuccess: () => {
      queryClient.invalidateQueries(QK.medicineLists);
      setOpenDelete(false);
      enqueueSnackbar("Deleted", { variant: "success" });
    },
  });

  const medicines = medicineLists?.results || [];
  const totalRecords = medicineLists?.count || 0;
  const totalPages = Math.ceil(totalRecords / medicinesPerPage);

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (medicine) => {
    setSelectedMedicine(medicine);
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setSelectedMedicine(null);
    setOpenEdit(false);
  };

  const handleOpenDelete = (medicine) => {
    setSelectedMedicine(medicine);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setSelectedMedicine(null);
    setOpenDelete(false);
  };

  const handleConfirmDelete = () => {
    if (selectedMedicine) {
      deleteMutation.mutate(selectedMedicine.id);
    }
  };

  const handlePageChange = (event, page) => setCurrentPage(page);

  const headers = [
    { key: "sl", label: "SL No." },
    { key: "name", label: "Name" },
    { key: "company", label: "Company Name" },
  ];

  const renderCell = (item, header) => {
    if (header.key === "sl") {
      return (
        <Typography sx={{ fontSize: "0.78rem", color: "#475467", fontWeight: 500 }}>
          {(currentPage - 1) * medicinesPerPage + medicines.indexOf(item) + 1}
        </Typography>
      );
    }
    if (header.key === "name") {
      return (
        <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#1A1A2E" }}>
          {item.name}
        </Typography>
      );
    }
    if (header.key === "company") {
      return (
        <Typography sx={{ fontSize: "0.78rem", color: "#475467" }}>
          {item.company || "-"}
        </Typography>
      );
    }
    return item[header.key] || "-";
  };

  return (
    <Box sx={{ width: "100%", p: 3, maxWidth: 1400, mx: "auto" }}>
      <Paper
        sx={{
          borderRadius: "12px",
          border: "1px solid #E2E8F0",
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "#F8FAFC",
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: alpha("#2469F0", 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MedicineIcon sx={{ color: "#2469F0", fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
                Medicine List
              </Typography>
              <Typography variant="caption" color="#64748b" sx={{ fontSize: "0.7rem" }}>
                Manage medicine records
              </Typography>
            </Box>
            <Chip
              label={`${totalRecords} item${totalRecords !== 1 ? "s" : ""}`}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.65rem",
                fontWeight: 600,
                bgcolor: alpha("#2469F0", 0.08),
                color: "#2469F0",
              }}
            />
          </Stack>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={handleOpenAdd}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "6px",
              bgcolor: "#2469F0",
              px: 3,
              py: 0.75,
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 2px 8px rgba(36, 105, 240, 0.3)",
              },
            }}
          >
            Add New
          </Button>
        </Box>

        <DataTable
          data={medicines}
          headers={headers}
          renderCell={renderCell}
          canUpdate={true}
          canDelete={true}
          onUpdate={(id) => {
            const medicine = medicines.find((m) => m.id === id);
            handleOpenEdit(medicine);
          }}
          onDelete={(id) => {
            const medicine = medicines.find((m) => m.id === id);
            handleOpenDelete(medicine);
          }}
          loading={isLoading}
        />
      </Paper>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
            size="small"
            sx={{
              "& .MuiPaginationItem-root": {
                fontSize: "0.8rem",
                fontWeight: 500,
              },
            }}
          />
        </Box>
      )}

      <AddMedicineModal open={openAdd} handleClose={handleCloseAdd} />
      {selectedMedicine && (
        <EditMedicineModal
          open={openEdit}
          handleClose={handleCloseEdit}
          medicine={selectedMedicine}
        />
      )}
      <ConfirmModal
        show={openDelete}
        handleClose={handleCloseDelete}
        handleConfirm={handleConfirmDelete}
        title="Confirm Deletion"
        body={`Are you sure you want to delete "${selectedMedicine?.name}"?`}
      />
    </Box>
  );
};

export default MedicineList;